import { api } from "../api/api";
import { CardDto } from "../common/api";
import { cardQuestionText } from "../utils/cardQuestionText";
import { CarGrade, CarItemAnswer, carReviewPayload, gradeCarCard } from "./carGrade";
import { CarPlayer, PlayOptions, PlayOutcome } from "./carPlayer";
import { CarCardPlan, CarItem, CarSegment, SttLanguage, buildCarPlan, speech } from "./carScript";
import { carSounds } from "./carSounds";
import { TaskQueue, withRetry } from "./taskQueue";
import { isCorrectAnswer } from "./voice/answerCheck";
import { soundWords } from "./voice/phonetic";
import { SpeechInput, SpeechStatus, Utterance } from "./voice/speechInput";
import { findCommand, HELP_TEXT, VoiceCommand } from "./voice/voiceCommands";

/** 'review' checks the spoken answers and rates the cards; 'listen' only plays them, nothing is saved. */
export type CarMode = 'review' | 'listen';

export type CarLimit = number | 'all';

export type CarStep =
    | 'starting'
    | 'loading'
    | 'intro'
    | 'question'
    | 'listen'
    | 'feedback'
    | 'announce'
    | 'end'
    | 'finished';

export interface CarItemResult {
    label: string;
    isCorrect?: boolean;
    /** What was heard as the answer. */
    heard?: string;
}

export interface CarCardResult {
    cardId: string;
    title: string;
    grade?: CarGrade;
    items: CarItemResult[];
    /** The card's audio could not be generated; it was skipped and stays due. */
    skipped?: boolean;
    /** Rated, but saving the review failed. */
    saveFailed?: boolean;
}

export interface CarSnapshot {
    mode: CarMode;
    step: CarStep;
    paused: boolean;
    cardNumber: number;
    cardTotal: number;
    itemNumber: number;
    itemCount: number;
    group?: string;
    results: CarCardResult[];
    noMoreCards: boolean;
    speech: SpeechStatus;
    /** The phrase being heard right now. */
    partial?: string;
    /** The last finished phrase and what it was taken for. */
    heard?: { text: string; meaning: string };
    /** Why the session could not run (no microphone…). */
    error?: string;
}

const EXTRA_CARDS = 10;
const SLOW_RATE = 0.8;
const MAX_SKIPPED_IN_A_ROW = 20;
/** How long to wait for an answer after the question, then once more after "Ваш ответ?". */
const ANSWER_WINDOW_MS = 12000;
const REPROMPT_WINDOW_MS = 8000;
/** Listen-only mode: the pause before the answer is played. */
const LISTEN_ONLY_PAUSE_MS = 4000;
const END_WINDOW_MS = 15000;
/** Speech that began this soon after our own clip ended may still be its echo. */
const ECHO_MARGIN_MS = 300;
/** ElevenLabs' concurrent-request limit for the current plan; extra requests get 429. */
const TTS_CONCURRENCY = 3;
const TTS_RETRY_DELAYS_MS = [1000, 3000];
/** Any number above the server's voice count works: it takes `variant % voices`. */
const VOICE_VARIANTS = 1000;

const ITEM_COMMANDS: ReadonlySet<VoiceCommand> = new Set<VoiceCommand>([
    'skip', 'nextCard', 'repeat', 'slower', 'dontKnow', 'progress', 'help',
]);
const END_COMMANDS: ReadonlySet<VoiceCommand> = new Set<VoiceCommand>([
    'yes', 'no', 'skip', 'nextCard', 'resume', 'finish',
]);
const PAUSED_COMMANDS: ReadonlySet<VoiceCommand> = new Set<VoiceCommand>([
    'resume', 'skip', 'yes', 'repeat', 'finish',
]);
const ANYTIME_COMMANDS: ReadonlySet<VoiceCommand> = new Set<VoiceCommand>(['pause', 'finish']);

const COMMAND_MEANINGS: Record<VoiceCommand, string> = {
    skip: 'next item',
    nextCard: 'next card',
    repeat: 'repeat',
    slower: 'slower',
    dontKnow: "don't know",
    pause: 'pause',
    resume: 'resume',
    finish: 'finish',
    progress: 'progress',
    help: 'help',
    yes: 'yes',
    no: 'no',
};

class Stopped extends Error {}
class Paused extends Error {}

/** Something the driver said (or pressed), as it matters right now. */
type Input =
    | { kind: 'answer'; correct: boolean; heard: string }
    | { kind: 'command'; command: VoiceCommand };

type Phase = 'question' | 'listen' | 'end';

interface ItemAudio {
    group?: string;
    question: string[];
    answer: string[];
    sentence: string[];
}

interface ItemOutcome {
    isCorrect?: boolean;
    heard?: string;
    /** Leave the rest of the card. */
    leaveCard?: boolean;
}

interface PendingCard {
    card: CardDto;
    plan: CarCardPlan;
    audio: Promise<ItemAudio[]>;
}

interface Fetched {
    pending?: PendingCard;
    totalDue: number;
}

export function plural(count: number, one: string, few: string, many: string): string {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11)
        return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14))
        return few;
    return many;
}

function segmentText(segment: CarSegment): string {
    return segment.kind === 'speech' ? segment.text : '___';
}

function itemLabel(item: CarItem): string {
    return item.question.map(segmentText).join(' ');
}

/** The languages to listen in for a card: Russian for the commands, plus each answer language. */
export function listeningLanguages(plan: CarCardPlan | undefined): SttLanguage[] {
    const languages: SttLanguage[] = ['ru'];
    plan?.items.forEach(item => {
        if (!languages.includes(item.answerLanguage))
            languages.push(item.answerLanguage);
    });
    return languages;
}

/**
 * One Car mode session, run by voice: every card item is read out in full
 * (the gap as a beep), then the driver says the missing form, or a command
 * ("дальше", "следующая карточка", "повтори", "не знаю"…). The answer is
 * checked by sound and the card rated from the answers. Due cards are fetched
 * one at a time and the next one is voiced while the current one plays.
 */
export class CarSession {
    private readonly player: CarPlayer;
    private readonly clips = new Map<string, Promise<string>>();
    private readonly seen: string[] = [];
    private readonly ttsQueue = new TaskQueue(TTS_CONCURRENCY);
    /** The announcer keeps one voice for the whole session. */
    private readonly announcerVariant = Math.floor(Math.random() * VOICE_VARIANTS);
    private state: CarSnapshot;
    private inbox: Utterance[] = [];
    /** A steering-wheel button press, taken as a command. */
    private buttonCommand: VoiceCommand | undefined;
    private finishRequested = false;
    /** What the session has said lately, to tell its own echo from the driver. */
    private spokenTexts: string[] = [];
    private speaking = false;
    private lastSpeechEnd = 0;
    /** The item being asked, and whether its answer is being waited for right now. */
    private currentItem: CarItem | undefined;
    private listening = false;
    private wakeLock: { release(): Promise<void> } | undefined;

    constructor(
        mode: CarMode,
        private readonly limit: CarLimit,
        private readonly speechInput: SpeechInput,
        private readonly onChange: (snapshot: CarSnapshot) => void,
    ) {
        this.state = {
            mode,
            step: 'starting',
            paused: false,
            cardNumber: 0,
            cardTotal: 0,
            itemNumber: 0,
            itemCount: 0,
            results: [],
            noMoreCards: false,
            speech: 'off',
        };
        this.player = new CarPlayer(paused => this.update({ paused }));
    }

    /** Call from a click handler: audio and the microphone may only start from a user gesture. */
    start(): void {
        // Starts the media element inside the click, which unlocks audio for the session.
        void this.player.play(carSounds().loading, { loop: true });
        this.keepScreenOn();
        this.speechInput.start({
            onUtterance: utterance => this.heard(utterance),
            onPartial: partial => this.update({ partial }),
            onStatus: status => this.update({ speech: status }),
        }).then(
            () => {
                this.speechInput.setLanguages(['ru']);
                void this.run();
            },
            error => {
                this.update({ error: `Microphone unavailable: ${error instanceof Error ? error.message : String(error)}` });
                this.stop();
                this.update({ step: 'finished' });
            },
        );
    }

    /** A steering-wheel button or an on-screen control: 'next' skips, 'prev' repeats. */
    command(command: 'next' | 'prev'): void {
        if (this.player.paused) {
            this.player.resume();
            return;
        }
        this.buttonCommand = command === 'next' ? 'skip' : 'repeat';
        this.player.interrupt();
    }

    togglePause(): void {
        this.player.togglePause();
    }

    stop(): void {
        this.player.stop();
        this.speechInput.stop();
        void this.wakeLock?.release().catch(() => undefined);
        this.wakeLock = undefined;
    }

    private update(patch: Partial<CarSnapshot>): void {
        this.state = { ...this.state, ...patch };
        this.onChange(this.state);
    }

    private get mode(): CarMode {
        return this.state.mode;
    }

    private keepScreenOn(): void {
        const wakeLock = (navigator as Navigator & {
            wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> };
        }).wakeLock;
        wakeLock?.request('screen').then(lock => {
            this.wakeLock = lock;
        }, () => undefined);
    }

    // ------------------------------------------------------------------ hearing

    /** A finished phrase from the recognizer. Pause and finish act at once; the rest waits for the session. */
    private heard(utterance: Utterance): void {
        const texts = this.transcriptsOf(utterance);
        if (texts.length === 0 || this.player.stopped)
            return;
        this.update({ partial: undefined });

        if (this.player.paused) {
            const command = findCommand(texts, PAUSED_COMMANDS);
            this.show(texts[0], command);
            if (command === 'finish') {
                this.finishRequested = true;
                this.player.resume();
            } else if (command !== undefined) {
                this.player.resume();
            }
            return;
        }

        if (this.isEcho(utterance, texts))
            return;

        const command = findCommand(texts, ANYTIME_COMMANDS);
        if (command === 'pause') {
            this.show(texts[0], command);
            this.player.pause();
            return;
        }
        if (command === 'finish') {
            this.show(texts[0], command);
            this.finishRequested = true;
            this.player.interrupt();
            return;
        }

        this.inbox.push(utterance);
        // While waiting, anything said is taken up at once. While reading out, only a
        // command or the right answer cuts in — not the radio or a passenger.
        const item = this.currentItem;
        const cutsIn = this.listening
            || findCommand(texts, this.state.step === 'end' ? END_COMMANDS : ITEM_COMMANDS) !== undefined
            || (item !== undefined && this.mode === 'review' && isCorrectAnswer(item.accepted, texts));
        if (cutsIn)
            this.player.interrupt();
    }

    private transcriptsOf(utterance: Utterance, first?: SttLanguage): string[] {
        const languages = Object.keys(utterance.transcripts) as SttLanguage[];
        if (first !== undefined)
            languages.sort((a, b) => (a === first ? -1 : b === first ? 1 : 0));
        return languages
            .map(language => (utterance.transcripts[language] ?? '').trim())
            .filter(text => text.length > 0);
    }

    private show(text: string, command: VoiceCommand | undefined, meaning?: string): void {
        this.update({ heard: { text, meaning: meaning ?? (command !== undefined ? COMMAND_MEANINGS[command] : 'not understood') } });
    }

    /**
     * Our own speech coming back through the microphone: it began while (or
     * just after) we were speaking and mostly repeats what we said.
     */
    private isEcho(utterance: Utterance, texts: string[]): boolean {
        if (!this.speaking && utterance.startedAt > this.lastSpeechEnd + ECHO_MARGIN_MS)
            return false;
        const spoken = new Set(this.spokenTexts.flatMap(soundWords));
        return texts.some(text => {
            const words = soundWords(text);
            return words.length > 0 && words.filter(word => spoken.has(word)).length * 2 >= words.length;
        });
    }

    /**
     * The next input that matters in this phase. While the item is still being
     * read out ('question') only a command or the right answer is taken; anything
     * else waits and counts as the answer once the reading is over.
     */
    private takeInput(item: CarItem | undefined, phase: Phase): Input | undefined {
        if (this.finishRequested)
            return { kind: 'command', command: 'finish' };
        if (this.buttonCommand !== undefined) {
            const command = this.buttonCommand;
            this.buttonCommand = undefined;
            return { kind: 'command', command };
        }

        const answers = item !== undefined && phase !== 'end' && this.mode === 'review';
        const allowed = phase === 'end' ? END_COMMANDS : ITEM_COMMANDS;

        for (let index = 0; index < this.inbox.length;) {
            const utterance = this.inbox[index];
            const texts = this.transcriptsOf(utterance, item?.answerLanguage);
            const take = (input: Input | undefined): Input | undefined => {
                this.inbox.splice(index, 1);
                return input;
            };

            if (answers && isCorrectAnswer(item!.accepted, texts)) {
                this.show(texts[0], undefined, 'right answer');
                return take({ kind: 'answer', correct: true, heard: texts[0] });
            }
            const command = findCommand(texts, allowed);
            if (command !== undefined) {
                this.show(texts[0], command);
                return take({ kind: 'command', command });
            }
            if (answers && phase === 'question') {
                index++;
                continue;
            }
            if (answers) {
                const heard = utterance.transcripts[item!.answerLanguage] ?? texts[0];
                this.show(heard, undefined, 'wrong answer');
                return take({ kind: 'answer', correct: false, heard });
            }
            this.show(texts[0], undefined);
            take(undefined);
        }
        return undefined;
    }

    /**
     * Plays clips one after another; something said (or a button) cuts them
     * short and is returned. Echo of our own speech is ignored and playback
     * goes on from the clip it cut.
     */
    private async playInterruptible(urls: string[], item: CarItem | undefined, options?: PlayOptions): Promise<Input | undefined> {
        for (let index = 0; index < urls.length;) {
            const outcome = await this.playSpeech(urls[index], options);
            const input = this.takeInput(item, 'question');
            if (input !== undefined)
                return input;
            if (outcome !== 'interrupted')
                index++;
        }
        return this.takeInput(item, 'question');
    }

    /** Waits for an answer or a command, up to `windowMs`. */
    private async listen(item: CarItem | undefined, windowMs: number, phase: Phase): Promise<Input | 'timeout'> {
        const deadline = Date.now() + windowMs;
        this.listening = true;
        try {
            for (;;) {
                const input = this.takeInput(item, phase);
                if (input !== undefined)
                    return input;
                const left = deadline - Date.now();
                if (left <= 0)
                    return 'timeout';

                const timer = setTimeout(() => this.player.interrupt(), left);
                try {
                    await this.play(carSounds().listening, { loop: true });
                } finally {
                    clearTimeout(timer);
                }
            }
        } finally {
            this.listening = false;
        }
    }

    // ------------------------------------------------------------------ the session

    private async run(): Promise<void> {
        try {
            const firstFetched = await this.waitFor(this.fetchNext());
            if (firstFetched.pending === undefined)
                this.update({ noMoreCards: true });
            const total = this.limit === 'all'
                ? firstFetched.totalDue
                : Math.min(this.limit, firstFetched.totalDue);
            this.update({ cardTotal: total });

            await this.guarded(() => this.say(this.mode === 'review'
                ? 'Поехали. Называйте пропущенное слово в нужной форме. Скажите «команды», чтобы услышать подсказки.'
                : 'Поехали. Режим прослушивания.'));

            let next: Promise<Fetched> | undefined = Promise.resolve(firstFetched);

            for (;;) {
                while (next !== undefined && this.state.cardNumber < this.state.cardTotal && !this.finishRequested) {
                    const fetched: Fetched = await this.waitFor(next);
                    if (fetched.pending === undefined) {
                        this.update({ noMoreCards: true, cardTotal: this.state.cardNumber });
                        next = undefined;
                        break;
                    }

                    const cardNumber = this.state.cardNumber + 1;
                    this.update({ cardNumber });
                    next = cardNumber < this.state.cardTotal ? this.fetchNext() : undefined;

                    await this.runCard(fetched.pending, cardNumber);
                }

                if (!(await this.endOfRound()))
                    break;

                this.update({ cardTotal: this.state.cardTotal + EXTRA_CARDS });
                next = this.fetchNext();
            }
        } catch (error) {
            if (!(error instanceof Stopped))
                throw error;
        } finally {
            this.stop();
            this.update({ step: 'finished', paused: false, partial: undefined });
            this.releaseClips();
        }
    }

    private progressText(): string {
        const remaining = Math.max(0, this.state.cardTotal - this.state.cardNumber);
        return `Карточка ${this.state.cardNumber} из ${this.state.cardTotal}. ` +
            `Осталось ${remaining} ${plural(remaining, 'карточка', 'карточки', 'карточек')}.`;
    }

    /** Announces the round's result; true when the driver asks for more cards. */
    private async endOfRound(): Promise<boolean> {
        this.update({ step: 'end', itemNumber: 0, itemCount: 0, group: undefined });
        this.speechInput.setLanguages(['ru']);
        const played = this.state.results.filter(result => result.skipped !== true).length;
        const canContinue = !this.state.noMoreCards && !this.finishRequested;

        let summary: string;
        if (played === 0) {
            summary = 'Карточек на повторение нет.';
        } else if (this.mode === 'review') {
            const clean = this.state.results.filter(result => result.grade === 'good').length;
            summary = `Готово. Без ошибок ${clean} из ${played}.`;
        } else {
            summary = `Готово. Прослушано ${played} ${plural(played, 'карточка', 'карточки', 'карточек')}.`;
        }
        if (this.state.noMoreCards && played > 0)
            summary = `Карточек на повторение больше нет. ${summary}`;

        this.finishRequested = false;
        await this.guarded(async () => {
            await this.play(carSounds().sessionEnd);
            await this.say(canContinue ? `${summary} Ещё десять карточек?` : summary);
        });
        if (!canContinue)
            return false;

        const answer = await this.guarded(() => this.listen(undefined, END_WINDOW_MS, 'end'));
        if (answer === 'timeout' || answer.kind !== 'command')
            return false;
        return answer.command !== 'no' && answer.command !== 'finish';
    }

    private async runCard(pending: PendingCard, cardNumber: number): Promise<void> {
        const { card, plan } = pending;
        const result: CarCardResult = {
            cardId: card.cardId,
            title: cardQuestionText(card.markup),
            items: plan.items.map(item => ({ label: itemLabel(item) })),
        };
        this.update({
            results: [...this.state.results, result],
            itemNumber: 0,
            itemCount: plan.items.length,
            group: undefined,
        });
        this.speechInput.setLanguages(listeningLanguages(plan));

        let audio: ItemAudio[];
        try {
            audio = await this.waitFor(pending.audio);
        } catch {
            this.patchResult(card.cardId, { skipped: true });
            await this.guarded(async () => {
                await this.play(carSounds().wrong);
                await this.say('Карточка пропущена.');
            });
            return;
        }

        const count = plan.items.length;
        const intro = plan.hasSubcards
            ? `Карточка ${cardNumber} из ${this.state.cardTotal}. ${count} ${plural(count, 'задание', 'задания', 'заданий')}.`
            : `Карточка ${cardNumber} из ${this.state.cardTotal}.`;
        this.player.setTitle(`Карточка ${cardNumber} из ${this.state.cardTotal}`, this.mode === 'review' ? 'Повторение' : 'Прослушивание');
        this.update({ step: 'intro' });
        await this.guarded(async () => {
            await this.play(carSounds().newCard);
            await this.say(intro);
        });

        const answers: CarItemAnswer[] = [];
        for (let index = 0; index < count && !this.finishRequested; index++) {
            const item = plan.items[index];
            this.update({ itemNumber: index + 1, group: item.group });
            // The answer joins these once it is said, so the driver saying it is not taken for echo.
            this.spokenTexts = item.question.map(segmentText);
            this.inbox = [];
            this.currentItem = item;
            const startedAt = Date.now();
            const outcome = await this.runItem(item, audio[index], verdict => this.patchItem(card.cardId, index, verdict));
            this.currentItem = undefined;
            answers.push({ subcard: item.subcard, isCorrect: outcome.isCorrect, timeSpent: Date.now() - startedAt });
            this.patchItem(card.cardId, index, outcome);
            if (outcome.leaveCard === true)
                break;
        }

        if (this.mode !== 'review')
            return;

        const grade = gradeCarCard(answers.map(answer => answer.isCorrect));
        if (grade === undefined)
            return;

        this.patchResult(card.cardId, { grade });
        api.applyReview(carReviewPayload(card, grade, answers))
            .catch(() => this.patchResult(card.cardId, { saveFailed: true }));

        const graded = answers.filter(answer => answer.isCorrect !== undefined);
        if (plan.hasSubcards && graded.length > 0 && !this.finishRequested) {
            const correct = graded.filter(answer => answer.isCorrect === true).length;
            await this.guarded(() => this.say(`${correct} из ${graded.length}.`));
        }
    }

    /**
     * One item: read it out, take the answer or a command, say what was right.
     * The verdict is reported before the answer is said back, so stopping
     * during that still keeps it.
     */
    private async runItem(item: CarItem, audio: ItemAudio, onVerdict: (outcome: ItemOutcome) => void): Promise<ItemOutcome> {
        const question = audio.group !== undefined ? [audio.group, ...audio.question] : audio.question;
        let phase: 'read' | 'listen' | 'reprompt' = 'read';
        let rate = 1;
        const conclude = async (outcome: ItemOutcome, kind: 'right' | 'wrong' | 'reveal'): Promise<ItemOutcome> => {
            onVerdict(outcome);
            const leave = await this.feedback(kind, audio);
            return leave ? { ...outcome, leaveCard: true } : outcome;
        };

        for (;;) {
            let input: Input | 'timeout' | undefined;
            if (phase === 'read') {
                this.update({ step: 'question' });
                const readRate = rate;
                input = await this.guarded(() => this.playInterruptible(question, item, { rate: readRate }));
                rate = 1;
                if (input === undefined) {
                    phase = 'listen';
                    if (this.mode === 'review')
                        await this.guarded(() => this.play(carSounds().yourTurn));
                    continue;
                }
            } else {
                this.update({ step: 'listen' });
                const window = this.mode === 'listen'
                    ? LISTEN_ONLY_PAUSE_MS
                    : phase === 'listen' ? ANSWER_WINDOW_MS : REPROMPT_WINDOW_MS;
                input = await this.guarded(() => this.listen(item, window, 'listen'));
            }

            if (input === 'timeout') {
                if (this.mode === 'review' && phase === 'listen') {
                    await this.guarded(() => this.say('Ваш ответ?'));
                    phase = 'reprompt';
                    continue;
                }
                // Listen-only mode, or no answer at all: say the answer, leave the item unrated.
                return conclude({}, 'reveal');
            }

            if (input.kind === 'answer')
                return conclude({ isCorrect: input.correct, heard: input.heard }, input.correct ? 'right' : 'wrong');

            switch (input.command) {
                case 'repeat':
                    phase = 'read';
                    continue;
                case 'slower':
                    phase = 'read';
                    rate = SLOW_RATE;
                    continue;
                case 'skip':
                    return {};
                case 'nextCard':
                case 'finish':
                    return { leaveCard: true };
                case 'dontKnow':
                    return conclude({ isCorrect: this.mode === 'review' ? false : undefined }, 'reveal');
                case 'progress':
                    await this.guarded(() => this.say(this.progressText()));
                    phase = phase === 'read' ? 'read' : 'listen';
                    continue;
                case 'help':
                    await this.guarded(() => this.say(HELP_TEXT));
                    phase = 'read';
                    continue;
                default:
                    continue;
            }
        }
    }

    /**
     * Says how it went: a right answer gets the chime and the full sentence; a
     * wrong one (or "не знаю", or silence) gets the answer, slowly. Returns true
     * when the driver asked for the next card meanwhile.
     */
    private async feedback(kind: 'right' | 'wrong' | 'reveal', audio: ItemAudio): Promise<boolean> {
        this.update({ step: 'feedback' });
        const item = this.currentItem;
        if (item !== undefined)
            this.spokenTexts = [...this.spokenTexts, ...item.answer.map(segmentText), ...item.sentence.map(segmentText)];
        const sounds = carSounds();
        const input = await this.guarded(async () => {
            if (kind === 'right') {
                await this.play(sounds.correct);
                return this.playInterruptible(audio.sentence, undefined);
            }
            if (kind === 'wrong')
                await this.play(sounds.wrong);
            await this.say(kind === 'wrong' ? 'Правильно:' : 'Ответ:');
            return this.playInterruptible(audio.answer, undefined, { rate: this.mode === 'review' ? SLOW_RATE : 1 });
        });
        return input?.kind === 'command' && (input.command === 'nextCard' || input.command === 'finish');
    }

    private patchResult(cardId: string, patch: Partial<CarCardResult>): void {
        this.update({
            results: this.state.results.map(result => (result.cardId === cardId ? { ...result, ...patch } : result)),
        });
    }

    private patchItem(cardId: string, index: number, outcome: ItemOutcome): void {
        this.update({
            results: this.state.results.map(result => (result.cardId !== cardId ? result : {
                ...result,
                items: result.items.map((item, i) => (i === index
                    ? { ...item, isCorrect: outcome.isCorrect, heard: outcome.heard }
                    : item)),
            })),
        });
    }

    // ------------------------------------------------------------------ audio

    /** Re-runs a step from its start after a pause (voice, phone call, navigation prompt, the pause button). */
    private async guarded<T>(step: () => Promise<T>): Promise<T> {
        for (;;) {
            try {
                return await step();
            } catch (error) {
                if (!(error instanceof Paused))
                    throw error;
                await this.player.waitForResume();
                if (this.player.stopped)
                    throw new Stopped();
            }
        }
    }

    private async play(url: string, options?: PlayOptions): Promise<PlayOutcome> {
        const outcome = await this.player.play(url, options);
        if (outcome === 'stopped')
            throw new Stopped();
        if (outcome === 'paused')
            throw new Paused();
        if (outcome === 'next' || outcome === 'prev') {
            this.buttonCommand = outcome === 'next' ? 'skip' : 'repeat';
            return 'interrupted';
        }
        return outcome;
    }

    /** A spoken clip: while it plays (and just after), what the microphone hears may be its echo. */
    private async playSpeech(url: string, options?: PlayOptions): Promise<PlayOutcome> {
        this.speaking = true;
        try {
            return await this.play(url, options);
        } finally {
            this.speaking = false;
            this.lastSpeechEnd = Date.now();
        }
    }

    /** Speaks a phrase; a phrase that cannot be voiced is left out. */
    private async say(text: string): Promise<PlayOutcome> {
        let url: string;
        try {
            url = await this.waitFor(this.clip(speech(text), this.announcerVariant, true));
        } catch (error) {
            if (error instanceof Stopped || error instanceof Paused)
                throw error;
            return 'ended';
        }
        const step = this.state.step;
        this.spokenTexts = [...this.spokenTexts, text];
        this.update({ step: 'announce' });
        const outcome = await this.playSpeech(url);
        this.update({ step });
        return outcome;
    }

    /**
     * Keeps the media element busy with the loading loop until the task settles, so
     * a slow fetch is heard as "loading" rather than silence and the media keys stay ours.
     */
    private async waitFor<T>(task: Promise<T>): Promise<T> {
        let settled = false;
        let looping = false;
        const onSettled = () => {
            settled = true;
            if (looping)
                this.player.interrupt();
        };
        void task.then(onSettled, onSettled);
        // A task that has already settled (a cached clip) flips `settled` before this resumes.
        await Promise.resolve();

        const previousStep = this.state.step;
        while (!settled) {
            this.update({ step: 'loading' });
            looping = true;
            const outcome = await this.player.play(carSounds().loading, { loop: true });
            looping = false;
            if (outcome === 'stopped')
                throw new Stopped();
            if (outcome === 'paused')
                await this.player.waitForResume();
            if (this.player.stopped)
                throw new Stopped();
        }
        if (this.state.step === 'loading')
            this.update({ step: previousStep });
        return task;
    }

    private async fetchNext(): Promise<Fetched> {
        let totalDue = 0;
        for (let attempt = 0; attempt < MAX_SKIPPED_IN_A_ROW; attempt++) {
            const response = await api.getNextReviewCard(undefined, this.seen);
            totalDue = Math.max(totalDue, response.dailyStat?.totalCardsForReview ?? 0);
            const card = response.card;
            if (card === undefined)
                return { totalDue };

            this.seen.push(card.cardId);
            const plan = buildCarPlan(card.markup);
            if (plan === undefined)
                continue;

            const audio = this.voice(plan);
            // Failures surface when the card is played; keep them from going unhandled meanwhile.
            audio.catch(() => undefined);
            return { pending: { card, plan, audio }, totalDue };
        }
        return { totalDue };
    }

    private voice(plan: CarCardPlan): Promise<ItemAudio[]> {
        // One speaker per language per card: the server maps the variant onto its list of voices.
        const variant = Math.floor(Math.random() * VOICE_VARIANTS);
        const clips = (segments: CarSegment[]) => Promise.all(segments.map(segment => this.clip(segment, variant)));
        return Promise.all(plan.items.map(async item => {
            const [group, question, answer, sentence] = await Promise.all([
                item.group !== undefined ? this.clip(speech(item.group), variant) : Promise.resolve(undefined),
                clips(item.question),
                clips(item.answer),
                clips(item.sentence),
            ]);
            return group !== undefined ? { group, question, answer, sentence } : { question, answer, sentence };
        }));
    }

    /** A voiced clip; `urgent` ones (announcements) skip ahead of the next card's prefetch. */
    private clip(segment: CarSegment, variant: number, urgent = false): Promise<string> {
        if (segment.kind === 'gap')
            return Promise.resolve(carSounds().gap);

        const key = `${segment.voice}|${variant}|${segment.text}`;
        const cached = this.clips.get(key);
        if (cached !== undefined)
            return cached;

        const request = () => this.ttsQueue.run(
            () => api.tts({ text: segment.text, voice: segment.voice, variant }),
            urgent,
        );
        const clip = withRetry(request, TTS_RETRY_DELAYS_MS)
            .then(blob => URL.createObjectURL(blob));
        clip.catch(() => this.clips.delete(key));
        this.clips.set(key, clip);
        return clip;
    }

    private releaseClips(): void {
        this.clips.forEach(clip => {
            clip.then(url => URL.revokeObjectURL(url), () => undefined);
        });
        this.clips.clear();
    }
}
